import { CdkConnectedOverlay, Overlay, ScrollStrategy } from '@angular/cdk/overlay';
import { CommonModule } from '@angular/common';
import {
    ChangeDetectionStrategy,
    Component,
    ElementRef,
    EventEmitter,
    HostListener,
    inject,
    Input,
    OnChanges,
    OnInit,
    Output,
    signal,
    SimpleChanges,
    TemplateRef,
    ViewChild
} from '@angular/core';
import { SafeHtml } from '@angular/platform-browser';
import { EditorComponentBase } from '../../classes/editor-component-base';
import { IGenericTypeAheadDropdownConfig } from '../../interfaces/type-ahead-dropdown-config.interface';
import { IdGeneratorPipe } from '../../pipes/id-generator.pipe';
import { IconSanitizerService } from '../../services/icon-sanitizer.service';
import { ObjectExtensionsService } from '../../services/object-extensions.service';
import { CDK_IMPORTS } from '../../shared/standalone-imports';
import { BadgeComponent } from '../badge/badge.component';
import { OptionsMenuComponent } from '../options-menu/options-menu.component';
import { SearchInputComponent } from '../search-input/search-input.component';

@Component({
    selector: 'lib-generic-dropdown-multiselect',
    templateUrl: './generic-dropdown-multiselect.component.html',
    styleUrls: ['./generic-dropdown-multiselect.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [CommonModule, IdGeneratorPipe, ...CDK_IMPORTS, BadgeComponent, SearchInputComponent, OptionsMenuComponent]
})
export class GenericDropdownMultiselectComponent<T> extends EditorComponentBase<T> implements OnInit, OnChanges {
    @ViewChild('dropdownContainer', { static: true }) dropdownContainer!: ElementRef<HTMLDivElement>;

    @ViewChild('overlayPanel') overlayPanel?: CdkConnectedOverlay;

    @ViewChild('searchInput')
    searchInput?: SearchInputComponent;

    @Input() source?: T[];

    @Input() config?: IGenericTypeAheadDropdownConfig<T>;

    @Input() disabled = false;

    @Input() selectedItems?: T[];

    @Output() selectedItemsChange = new EventEmitter<T[]>();

    @Output() removeItemChange = new EventEmitter<T>();

    @Input() override isReadOnly?: boolean;

    @Input() disabledInputTypeText? = false;

    @Input() removeKey?: string;

    @Input() noResultsText? = 'No matches found';

    @Input() placeholder?: string;

    @Input() ariaLabel?: string;

    @Input() text = '';

    @Input() error? = false;

    @Input() multiselectId?: string;

    sourceItems?: T[] = [];
    menuOpen = false;

    focusList? = false;

    overlayWidth = signal<number>(0);
    protected scrollStrategy!: ScrollStrategy;

    private _originalItems?: T[];

    private readonly objectExtensionsService = inject(ObjectExtensionsService);
    private readonly overlay = inject(Overlay);
    private readonly iconSanitizer = inject(IconSanitizerService);

    readonly arrowDownIcon: SafeHtml = this.iconSanitizer.getIcon('arrowDown');
    readonly arrowUpIcon: SafeHtml = this.iconSanitizer.getIcon('arrowUp');

    ngOnInit(): void {
        this.scrollStrategy = this.overlay.scrollStrategies.close();
        this.config = {
            ...this.config,
            multiple: this.config?.multiple ?? true
        } as IGenericTypeAheadDropdownConfig<T>;
        if (!this.selectedItems) this.selectedItems = [];
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['selectedItems']?.currentValue) {
            this.selectedItems = this.selectedItems ? [...this.selectedItems] : undefined;
        }
        if (changes['source']?.firstChange) {
            this._originalItems = this.objectExtensionsService.clone(this.source) as T[] | undefined;
            this.sourceItems = this.source;
        }
    }

    /**
     * Set the selected item
     * @param item Item clicked
     */
    selectItem(item: T): void {
        if (!this.isSelected(item)) {
            (this.selectedItems as T[]).push(item);
            this.sourceItems = this.updateList(this.sourceItems);
            if (this.selectedItems?.length) this.emitItemsChanged(this.selectedItems);
            if (!this.sourceItems?.length) this.closePanel();
            else if (this.menuOpen) this.searchInput?.inputElement?.nativeElement.focus();
        }
    }

    updateList(list: T[] | undefined): T[] | undefined {
        if (list?.length) {
            this.updateOverlayPosition();
            return this.removeSelectedItems(list);
        }

        return [];
    }

    isSelected(item: T): boolean {
        if (!this.selectedItems?.length) return false;

        const key = this.removeKey ?? 'id';
        return this.selectedItems.some(
            (selectedItem) => (selectedItem as Record<string, unknown>)[key] === (item as Record<string, unknown>)[key]
        );
    }

    emitItemsChanged(items: T[]): void {
        this.selectedItemsChange.emit(items);
    }

    onKeyDown(event: KeyboardEvent): void {
        if (event.key === 'Escape' && this.menuOpen) {
            this.closePanel();
            event.stopPropagation();
            return;
        }

        if (this.source && this.source.length > 0) {
            if (event.key === 'Enter') {
                this.openOptionsMenu();
            } else if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
                event.preventDefault();
                this.focusList = true;
            } else if (event.key === 'Tab') {
                if (this.menuOpen) this.closePanel();
            }
        }
    }

    onOptionsKeyDown(event: KeyboardEvent): void {
        // OptionsMenu closes the panel before emitting listKeyDown.
        if (event.key === 'Escape') event.stopPropagation();
    }

    removeItem(item: T) {
        const objectIndex = this.selectedItems?.indexOf(item);
        if (objectIndex != undefined && objectIndex >= 0) this.selectedItems?.splice(objectIndex, 1);

        this.sourceItems = this.removeSelectedItems(this.source);
        this.getItemsAndEmitChanges();
        this.removeItemChange.emit(item);
        if (this.selectedItems?.length === 0) this.menuOpen = false;
    }

    async onTextChanged(event: string): Promise<void> {
        if (event === '') {
            this.sourceItems = this.source;
        }

        this.sourceItems = this.updateList(
            this.source?.filter((item) =>
                (item[this.config?.itemDisplayKey as keyof T] as string).toLowerCase().includes(event.toLowerCase())
            )
        );
    }

    openOptionsMenu(): void {
        if (this.isReadOnly) return;
        this.sourceItems = this.updateList(this.source);
        this.overlayWidth.set(this.dropdownContainer.nativeElement.offsetWidth);
        this.menuOpen = !this.menuOpen;
        this.text = '';
        this.focusInput();
    }

    getTemplate(): TemplateRef<unknown> | null {
        if (!this.config?.itemTemplate) return null;
        return this.config.itemTemplate;
    }

    closePanel() {
        this.menuOpen = false;
        this.text = '';
        this.focusList = false;
        this.dropdownContainer.nativeElement.focus();
    }

    focusInput() {
        if (this.menuOpen) {
            setTimeout(() => {
                this.searchInput?.inputElement?.nativeElement.focus();
            }, 0);
        }
    }

    onMenuOpenChange(): void {
        if (!this.menuOpen) this.closePanel();
    }

    @HostListener('window:resize')
    onWindowResize(): void {
        this.overlayWidth.set(this.dropdownContainer.nativeElement.offsetWidth);
    }

    private removeSelectedItems(source: T[] | undefined): T[] | undefined {
        if (!source) return;

        if (this.selectedItems?.length !== undefined) {
            const key = this.removeKey ?? 'id';
            return source.filter(
                (sourceItem) =>
                    !this.selectedItems?.some(
                        (selectedItem) =>
                            (selectedItem as Record<string, unknown>)[key] ===
                            (sourceItem as Record<string, unknown>)[key]
                    )
            );
        } else {
            return source;
        }
    }

    private getItemsAndEmitChanges(): void {
        const items = this.selectedItems ? [...this.selectedItems] : undefined;
        if (this.menuOpen) this._originalItems = this.objectExtensionsService.clone(items) as T[] | undefined;
        this.emitItemsChanged(items || []);
        if (items) if (this.objectExtensionsService.isEqualTo(items, this._originalItems)) return;
    }

    private updateOverlayPosition() {
        setTimeout(() => {
            if (!this.overlayPanel?.overlayRef) return;
            this.overlayPanel.overlayRef.updatePosition();
        }, 0);
    }
}
